import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { routeFilesApi, type RoutingMovementApi, type RoutingRouteReadApi } from '../routingApi';
import { fileTooLarge } from '../routingForm';
import {
  PURPOSE_LABEL,
  ROUTE_PURPOSE,
  type DocumentRouteItem,
  type RoutePurpose,
  type RoutingDesk,
  type RoutingUnitOption,
} from '../routingTypes';
import { useUnitStaff } from '../useRoutingDesk';
import { fileAll } from '../utils/fileAttachments';
import { sampleRoute } from '../utils/sampleRouting';
import { RoutingFilesInput } from './RoutingFilesInput';

const ID_PREFIX = 'route-document';

const PURPOSE_OPTIONS = Object.values(ROUTE_PURPOSE).map((p) => ({ value: String(p), label: PURPOSE_LABEL[p] }));

/**
 * Send a document on from the caller's unit to another — a received hard copy, or a dispatch its
 * Inbox has taken in. `api` is whichever of the two it moves; the endpoints have one shape.
 *
 * **FROM is not asked** — it is the unit of the caller's own post, which the server reads. The form
 * states it. The document stays that unit's until the unit it is sent to — or the one employee
 * named — acknowledges it, and the unit may route it again meanwhile: each recipient that takes
 * it in ends with a copy (10 Oct 2026).
 *
 * **The employee is optional** and drawn only for a reader holding `personnel.read` — the picker
 * lists names through the masked personnel path, and without that key there is no list to offer.
 * Named, ONLY that employee may acknowledge it.
 *
 * **Files go up AFTER the line exists**, one request each, against the forward — the dispatch
 * form's shape (`fileAll`). A file that fails leaves the routing standing and says so.
 *
 * `verb` is the page's word for the act — Receiving calls it Dispatch (product call, 10 Oct 2026).
 */
export function RouteDocumentModal({
  open,
  api,
  document,
  verb = 'Route',
  onClose,
  onSaved,
}: {
  open: boolean;
  api: RoutingRouteReadApi & RoutingMovementApi;
  document: {
    id: string;
    trackingNumber: string;
    holders: RoutingUnitOption[];
    pendingRoutes?: DocumentRouteItem[];
  };
  verb?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [toId, setToId] = useState('');
  const [personnelId, setPersonnelId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const [units, setUnits] = useState<RoutingUnitOption[]>([]);
  const [desk, setDesk] = useState<RoutingDesk | null>(null);
  const [loading, setLoading] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const staff = useUnitStaff(toId);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([api.units(controller.signal).catch(() => []), api.desk(controller.signal).catch(() => null)])
      .then(([unitRows, deskRow]) => {
        if (controller.signal.aborted) return;
        setUnits(unitRows);
        setDesk(deskRow);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [api]);

  /*
    Offer only what can be chosen: not the caller's own unit, not a unit already holding it, and not
    one it is already on its way to — the server refuses all three.
  */
  const unitOptions = useMemo(() => {
    const taken = new Set([
      ...(desk?.orgUnitId != null ? [desk.orgUnitId] : []),
      ...document.holders.map((h) => h.id),
      ...(document.pendingRoutes ?? []).map((p) => p.toOrgUnitId).filter((id): id is string => id != null),
    ]);

    return units
      .filter((u) => !taken.has(u.id))
      .map((u) => ({ value: u.id, label: u.name, sublabel: u.code ?? undefined, keywords: u.code ?? undefined }));
  }, [units, desk, document.holders, document.pendingRoutes]);

  const canSave = toId !== '' && purpose !== '' && !files.some(fileTooLarge) && confirmed && !saving;

  function pickUnit(next: string) {
    setToId(next);
    // The employee belongs to the unit — a different unit clears them.
    setPersonnelId('');
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const line = await api.route(document.id, {
        toOrgUnitId: toId,
        toPersonnelId: personnelId || null,
        purpose: Number(purpose) as RoutePurpose,
        remarks: remarks.trim() || null,
      });

      if (files.length > 0) {
        setProgress(0);
        const failed = await fileAll(routeFilesApi(api, document.id, line.id), line.id, files, setProgress);

        if (failed.length > 0) {
          toast.failure(
            `${document.trackingNumber} was sent, but ${failed.map((f) => `${f.name} (${f.reason})`).join('; ')} could not be attached. Attach ${failed.length === 1 ? 'it' : 'them'} again from the Routing tab.`,
          );
        }
      }

      const to = units.find((u) => u.id === toId)?.name ?? 'the unit';
      toast.success(`Sent ${document.trackingNumber} to ${to}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError(`Could not ${verb.toLowerCase()} this document.`, 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${verb} ${document.trackingNumber}`}
      size="lg"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleRoute(unitOptions.map((o) => o.value));
          pickUnit(sample.toOrgUnitId);
          setPurpose(String(sample.purpose));
          setRemarks(sample.remarks);
          // The employee is left alone — their list loads only once the unit is chosen.
        },
        disabled: saving || loading,
      }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            // A REAL figure while files go up — `apiUpload` measures it.
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The consequence, stated before the tick — not a field, so it is a sentence. */}
        <p className="text-sm text-slate-600">
          It leaves <span className="font-medium text-slate-800">{desk?.orgUnitName ?? 'your unit'}</span> and is that
          unit&apos;s until the unit you send it to — or the employee you name — acknowledges it. You can send it to another
          unit meanwhile; each unit that takes it in keeps a copy.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-to-org-unit-id`}
            label="Send to"
            required
            description="The unit that takes it next. Units already holding it, or already sent it, are not offered."
            value={toId}
            onChange={pickUnit}
            options={unitOptions}
            remote
            loading={loading}
            disabled={saving}
            error={error?.fieldError('toOrgUnitId')}
          />

          <Select
            id={`${ID_PREFIX}-purpose`}
            label="Action requested"
            required
            description="What that unit is asked to do with it — the box a routing slip ticks."
            value={purpose}
            onChange={setPurpose}
            options={PURPOSE_OPTIONS}
            sort="none"
            searchable={false}
            disabled={saving}
            error={error?.fieldError('purpose')}
          />

          {staff.canRead ? (
            <div className="sm:col-span-2">
              <Select
                id={`${ID_PREFIX}-to-personnel-id`}
                label="Addressed to"
                description={
                  toId === ''
                    ? 'Choose the unit first — this lists the employees posted there.'
                    : 'Optional — an employee of that unit. Named, only they can acknowledge it; otherwise anyone posted there can.'
                }
                value={personnelId}
                onChange={setPersonnelId}
                options={staff.options}
                remote
                loading={staff.loading}
                disabled={saving || toId === ''}
                error={error?.fieldError('toPersonnelId')}
              />
            </div>
          ) : null}
        </div>

        <RoutingFilesInput id={`${ID_PREFIX}-files`} files={files} onChange={setFiles} disabled={saving} />

        <Textarea
          id={`${ID_PREFIX}-remarks`}
          label="Remarks"
          description="A note for the unit it is sent to."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          disabled={saving}
          error={error?.fieldError('remarks')}
        />
      </div>
    </Modal>
  );
}
